import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfDay, endOfDay } from "date-fns";
import { useBranchContext } from "@/hooks/useBranchContext";

interface StepTATData {
  step: string;
  name: string;
  avgMinutes: number;
  withinTAT: number;
  level1Breach: number;
  level2Breach: number;
  level3Breach: number;
  totalCompleted: number;
}

// Workflow steps in order with display names (for dashboard chart)
const WORKFLOW_STEPS = [
  { step: "DSS", name: "Discharge Summary Saved" },
  { step: "DSF", name: "Discharge Summary Approved" },
  { step: "CS", name: "Charge Sheet" },
  { step: "PC_PHARMACY", name: "Pharmacy Clearance" },
  { step: "BS", name: "Bill Saved" },
  { step: "BA", name: "Bill Approved" },
  { step: "PC", name: "Payment Complete" },
  { step: "PD", name: "Physical Discharge" },
];

// Full workflow order for inferring "previous step" when started_at is missing (e.g. HIS-synced)
const STEP_ORDER = ["MD", "DSS", "DSF", "CS", "PC_PHARMACY", "BS", "BA", "PC", "PD"];

function getPreviousStep(step: string): string | null {
  const i = STEP_ORDER.indexOf(step);
  return i > 0 ? STEP_ORDER[i - 1]! : null;
}

// Default TAT thresholds in minutes (fallback if not in database)
const DEFAULT_TAT_THRESHOLDS: Record<string, { allowed: number; grace: number; level1: number; level2: number }> = {
  DSS: { allowed: 45, grace: 15, level1: 80, level2: 100 },
  DSF: { allowed: 30, grace: 15, level1: 80, level2: 100 },
  CS: { allowed: 30, grace: 15, level1: 80, level2: 100 },
  PC_PHARMACY: { allowed: 45, grace: 15, level1: 80, level2: 100 },
  BS: { allowed: 30, grace: 15, level1: 80, level2: 100 },
  BA: { allowed: 30, grace: 15, level1: 80, level2: 100 },
  PC: { allowed: 60, grace: 15, level1: 80, level2: 100 },
  PD: { allowed: 30, grace: 15, level1: 80, level2: 100 },
};

export const useStepwiseTATStats = () => {
  const { currentBranchId } = useBranchContext();
  return useQuery({
    queryKey: ["stepwise-tat-stats-24h", currentBranchId],
    queryFn: async (): Promise<StepTATData[]> => {
      if (currentBranchId == null) return [];
      // Use "today" window instead of rolling 24h so dashboard
      // matches operational reporting expectations.
      const today = new Date();
      const todayStart = startOfDay(today).toISOString();
      const todayEnd = endOfDay(today).toISOString();

      // Fetch TAT configurations from database
      const { data: tatConfigs } = await supabase
        .from("tat_configurations")
        .select("workflow_step, allowed_tat_minutes, level_1_threshold_percentage, level_2_threshold_percentage, grace_period_minutes")
        .eq("is_active", true);

      // Build a lookup map from database configs
      const configMap: Record<string, { allowed: number; grace: number; level1: number; level2: number }> = {};
      (tatConfigs || []).forEach((config) => {
        if (config.workflow_step) {
          configMap[config.workflow_step] = {
            allowed: config.allowed_tat_minutes,
            grace: config.grace_period_minutes || 15,
            level1: config.level_1_threshold_percentage || 80,
            level2: config.level_2_threshold_percentage || 100,
          };
        }
      });

      // Fetch completed activities today (include patient_id for previous-step inference)
      const { data: activities, error } = await supabase
        .from("activities")
        .select("patient_id, workflow_step, time_taken_minutes, started_at, completion_time")
        .eq("status", "completed")
        .not("workflow_step", "is", null)
        .eq("branch_id", currentBranchId)
        .gte("completion_time", todayStart)
        .lte("completion_time", todayEnd);

      if (error) throw error;

      // Per-patient map: workflow_step -> { completion_time, started_at, time_taken_minutes } (latest by completion_time)
      const byPatient = new Map<string, Map<string, { completion_time: string; started_at: string | null; time_taken_minutes: number | null }>>();
      (activities || []).forEach((a) => {
        if (!a.patient_id || !a.completion_time) return;
        if (!byPatient.has(a.patient_id)) byPatient.set(a.patient_id, new Map());
        const patientSteps = byPatient.get(a.patient_id)!;
        const existing = patientSteps.get(a.workflow_step);
        if (!existing || new Date(a.completion_time).getTime() > new Date(existing.completion_time).getTime()) {
          patientSteps.set(a.workflow_step, {
            completion_time: a.completion_time,
            started_at: a.started_at ?? null,
            time_taken_minutes: a.time_taken_minutes ?? null,
          });
        }
      });

      // Compute duration for each activity: use time_taken_minutes, or (completion - started_at), or (completion - previous step completion)
      const stepToDurations = new Map<string, number[]>();
      WORKFLOW_STEPS.forEach(({ step }) => stepToDurations.set(step, []));
      const seenPatientStep = new Set<string>();

      (activities || []).forEach((a) => {
        if (!a.patient_id || !a.completion_time || !STEP_ORDER.includes(a.workflow_step)) return;
        const key = `${a.patient_id}:${a.workflow_step}`;
        if (seenPatientStep.has(key)) return;
        let durationMinutes: number | null = null;
        if (a.time_taken_minutes != null && a.time_taken_minutes > 0) {
          durationMinutes = a.time_taken_minutes;
        } else if (a.started_at && a.completion_time) {
          const startMs = new Date(a.started_at).getTime();
          const endMs = new Date(a.completion_time).getTime();
          if (endMs > startMs) durationMinutes = (endMs - startMs) / (1000 * 60);
        }
        if (durationMinutes == null || durationMinutes <= 0) {
          const prevStep = getPreviousStep(a.workflow_step);
          if (prevStep) {
            const patientSteps = byPatient.get(a.patient_id);
            const prev = patientSteps?.get(prevStep);
            if (prev) {
              const endMs = new Date(a.completion_time).getTime();
              const startMs = new Date(prev.completion_time).getTime();
              if (endMs > startMs) durationMinutes = (endMs - startMs) / (1000 * 60);
            }
          }
        }
        if (durationMinutes != null && durationMinutes > 0) {
          stepToDurations.get(a.workflow_step)?.push(durationMinutes);
          seenPatientStep.add(key);
        }
      });

      // Process data for each workflow step
      const stepStats: StepTATData[] = WORKFLOW_STEPS.map(({ step, name }) => {
        const timeTakenList = stepToDurations.get(step) ?? [];

        // Get thresholds from database config or use defaults
        const thresholds = configMap[step] || DEFAULT_TAT_THRESHOLDS[step] || { allowed: 30, grace: 15, level1: 80, level2: 100 };

        if (timeTakenList.length === 0) {
          return {
            step,
            name,
            avgMinutes: 0,
            withinTAT: 0,
            level1Breach: 0,
            level2Breach: 0,
            level3Breach: 0,
            totalCompleted: 0,
          };
        }

        const avgMinutes = timeTakenList.reduce((sum, t) => sum + t, 0) / timeTakenList.length;
        
        // Calculate thresholds in minutes
        const level1ThresholdMinutes = thresholds.allowed * (thresholds.level1 / 100); // 80% of allowed
        const level2ThresholdMinutes = thresholds.allowed; // 100% of allowed
        const level3ThresholdMinutes = thresholds.allowed + thresholds.grace; // allowed + grace

        let withinTAT = 0;
        let level1Breach = 0;
        let level2Breach = 0;
        let level3Breach = 0;

        timeTakenList.forEach((time) => {
          if (time <= level1ThresholdMinutes) {
            withinTAT++;
          } else if (time <= level2ThresholdMinutes) {
            level1Breach++;
          } else if (time <= level3ThresholdMinutes) {
            level2Breach++;
          } else {
            level3Breach++;
          }
        });

        const total = timeTakenList.length;

        return {
          step,
          name,
          avgMinutes,
          withinTAT: Math.round((withinTAT / total) * 100),
          level1Breach: Math.round((level1Breach / total) * 100),
          level2Breach: Math.round((level2Breach / total) * 100),
          level3Breach: Math.round((level3Breach / total) * 100),
          totalCompleted: total,
        };
      });

      return stepStats;
    },
    enabled: currentBranchId != null,
    refetchInterval: 30000,
  });
};
